import rawCatalog from '../assets/furniture_catalog.json';
import rawCategories from '../assets/furniture_categories.json';
import { findSheet, parseStampId, SHEET_TILE_SIZE, type SheetStamp } from './sheetCatalog.js';

/**
 * Catalogue mobilier — généré par `tools/assets-cli/src/ingest-furniture.ts`
 * à partir de `tools/assets-cli/furniture-curation.json` (sélection manuelle).
 *
 * IDs stables : `lz_{theme}_{n}` où `n` est le numéro LimeZu du fichier source
 * (`..._Singles_32x32_{n}.png`) — ne dépend plus de l'ordre des fichiers dans
 * le dossier, contrairement à l'ancien format `{theme}_{NNN}` (compteur
 * séquentiel, décalé dès qu'un fichier était ajouté/retiré).
 */
export interface FurnitureCatalogEntry {
  id: string;
  url: string;
  /** Clé de FURNITURE_CATEGORIES pour une entrée visible ; thème LimeZu d'origine pour une entrée masquée. */
  category: string;
  label: string;
  dimensions: { w: number; h: number };
  tileSize: number;
  anchor: { x: number; y: number };
  collision: null;
  /**
   * Masquée de la palette de l'éditeur (hors sélection) — conservée
   * uniquement pour que le mobilier déjà posé continue de s'afficher.
   */
  hidden?: boolean;
  /**
   * Ancien ID séquentiel (`kitchen_137`), résolu par findFurniture() tant
   * que des lignes `map_furniture` peuvent encore le référencer (migration
   * `20260924000000_furniture_stable_ids` pas encore jouée sur un env).
   */
  legacyId?: string;
  /**
   * Pont, ponton : rend l'eau peinte franchissable sur ses cases, quel que
   * soit son calque (ZoneScene.rebuildWaterBodies) ; posé d'office « au sol »
   * par l'éditeur. Liste : WALKABLE_FURNITURE (tools/assets-cli).
   */
  walkable?: boolean;
  /**
   * Cases réellement praticables d'un objet walkable, rangées du haut vers
   * le bas, '1' = planches (le pied d'un ponton dessiné dans l'eau reste
   * bloquant). Absent = toute l'emprise.
   */
  walkableTiles?: string[];
  /**
   * Rectangle de planche publiée dont l'objet est issu (voir sheetCatalog) —
   * permet d'en poser une partie depuis la palette. Tampon hors catalogue
   * (onglet Planches) : `url` vide, l'image est découpée à l'affichage dans
   * les pages de la planche.
   */
  stamp?: SheetStamp;
  /**
   * Objet animé : `url` est une bande horizontale de `frames` images de
   * `frameWidth`×`frameHeight` (= `dimensions`), jouée en boucle.
   */
  anim?: { frameWidth: number; frameHeight: number; frames: number; frameRate: number };
  /**
   * Objet debout (plante, arbre, lampadaire, fontaine, armoire…) : seule sa
   * base bloque, l'avatar passe derrière le haut (tri en Y), comme les arbres
   * d'une carte Tiled. Grille de collision lue dans l'image à l'import
   * (`tools/assets-cli/src/furniture-standing.ts`) : la base réelle — le
   * tronc, le pied, le bassin — pas toute la largeur. Imposé au rendu, quels
   * que soient le calque et la collision enregistrés (`standingPlacement`).
   */
  standing?: string[];
  /**
   * Ligne de tri en Y remontée de `sortLift` px au-dessus du bas de l'image :
   * bâtiment à perron, dont on monte les marches (cases libres) jusqu'à la
   * porte sur le palier. Sur les marches l'avatar passe devant, au-dessus de
   * cette ligne il passe derrière la façade. Absent = bas de l'image.
   */
  sortLift?: number;
  /**
   * Le meuble est lui-même un poste (pupitre du campus) : un emplacement de
   * bureau peut le recouvrir (`furnitureBlocksDesk`).
   */
  deskBase?: boolean;
  /** Monde du campus de Charles (IDs `ch_*`) : palette filtrée par thème, on ne mélange pas les deux mondes sur une carte. */
  theme?: 'campus';
  /** Se pose « au sol » d'office (tapis, marquages), sous les avatars. */
  floorLayer?: boolean;
  /** Bâtiment : cases de la porte depuis le coin haut gauche (`y` = seuil), où poser le portail vers l'intérieur. */
  door?: { x: number; y: number; w: number };
  /** Bâtiment : modèle d'intérieur lié (paquet campus). */
  interior?: string;
}

/** Catégories métier de la palette (ordre d'affichage). Partagé avec tools/assets-cli. */
export const FURNITURE_CATEGORIES = rawCategories as Array<{ id: string; label: string }>;

export const furnitureCatalog = rawCatalog as FurnitureCatalogEntry[];

const byId = new Map<string, FurnitureCatalogEntry>();
for (const entry of furnitureCatalog) {
  const stamp = parseStampId(entry.id);
  if (stamp) entry.stamp = stamp;
  byId.set(entry.id, entry);
  if (entry.legacyId) byId.set(entry.legacyId, entry);
}

/**
 * Résout un ID stable, un ancien ID séquentiel, ou un tampon de planche
 * publiée (`lz_{planche}_r{x}_{y}_{l}x{h}[_fx]`, hors catalogue) vers son
 * entrée. Les tampons sont synthétisés à la demande puis mémorisés.
 */
export function findFurniture(id: string): FurnitureCatalogEntry | undefined {
  const known = byId.get(id);
  if (known) return known;
  const stamp = parseStampId(id);
  if (!stamp) return undefined;
  const entry: FurnitureCatalogEntry = {
    id,
    url: '',
    category: 'sheet',
    label: `${findSheet(stamp.sheet)!.label} - ${stamp.tw}×${stamp.th}`,
    dimensions: { w: stamp.tw * SHEET_TILE_SIZE, h: stamp.th * SHEET_TILE_SIZE },
    tileSize: SHEET_TILE_SIZE,
    anchor: { x: 0.5, y: 1 },
    collision: null,
    stamp,
  };
  byId.set(id, entry);
  return entry;
}

// ── Vues d'un même objet (orientation) ────────────────────────

/**
 * Vue d'un objet dessiné en vue de dessus de trois quarts : face, dos et
 * profils sont des dessins différents (pas de rotation d'image possible).
 * Chaque vue est une entrée du catalogue dont l'id finit par `-face`, `-dos`,
 * `-droite`, `-gauche` (ou `_…` pour les objets Placio et LimeZu) ; les
 * entrées d'un même id sans ce suffixe forment une famille, un seul objet
 * dans la palette de l'éditeur. Un objet sans suffixe dont il existe une vue
 * (`pl_ecran` + `pl_ecran_dos`, canapé LimeZu + son dos reconstruit) est la
 * vue de face de sa famille. Une vue absente n'est jamais fabriquée au rendu :
 * le miroir de la vue opposée existe seulement s'il a été produit à l'import
 * (`"miroir": true` du paquet de Charles, voir ingest-furniture).
 */
export type FurnitureView = 'face' | 'droite' | 'dos' | 'gauche';

/** Ordre de passage d'une vue à l'autre (touche R de l'éditeur). */
export const FURNITURE_VIEW_ORDER: readonly FurnitureView[] = ['face', 'droite', 'dos', 'gauche'];

export const FURNITURE_VIEW_LABELS: Record<FurnitureView, string> = {
  face: 'Face', droite: 'Droite', dos: 'Dos', gauche: 'Gauche',
};

const VIEW_ID = /^(.+?)[-_](face|dos|droite|gauche)$/;

/**
 * Familles dont `-gauche`/`-droite` ne sont pas des vues mais des pièces
 * différentes (bouts gauche et droit d'une haie, à poser chacun à sa place).
 */
const NOT_VIEWS = new Set(['ch_haie']);

interface FurnitureFamily { id: string; views: Map<FurnitureView, FurnitureCatalogEntry> }

const familyOf = new Map<string, FurnitureFamily>();
{
  const families = new Map<string, FurnitureFamily>();
  for (const entry of furnitureCatalog) {
    const m = VIEW_ID.exec(entry.id);
    if (!m || NOT_VIEWS.has(m[1]!)) continue;
    let family = families.get(m[1]!);
    if (!family) families.set(m[1]!, family = { id: m[1]!, views: new Map() });
    const view = m[2] as FurnitureView;
    // Deux entrées pour une même vue (`x-dos` et `x_dos`) : la visible gagne.
    const prev = family.views.get(view);
    if (!prev || (prev.hidden && !entry.hidden)) family.views.set(view, entry);
  }
  for (const family of families.values()) {
    const plain = byId.get(family.id);
    if (plain && !family.views.has('face')) family.views.set('face', plain);
    // Une vue seule n'est pas une famille (rien à faire défiler).
    if (family.views.size < 2) continue;
    for (const entry of family.views.values()) familyOf.set(entry.id, family);
  }
}

/** Vue d'un objet (null : objet sans vues). */
export function furnitureView(id: string): FurnitureView | null {
  const family = familyOf.get(id);
  if (!family) return null;
  for (const [view, entry] of family.views) if (entry.id === id) return view;
  return null;
}

/** Id de la famille d'un objet (lui-même s'il n'a pas de vues). */
export function furnitureFamilyId(id: string): string {
  return familyOf.get(id)?.id ?? id;
}

/**
 * Vues disponibles d'un objet, dans l'ordre face, droite, dos, gauche :
 * vues visibles dans la palette, plus l'objet lui-même s'il est masqué (déjà
 * posé). Objet sans vues : une seule entrée, vue nulle.
 */
export function orientations(id: string): Array<{ view: FurnitureView | null; entry: FurnitureCatalogEntry }> {
  const family = familyOf.get(id);
  if (!family) {
    const entry = findFurniture(id);
    return entry ? [{ view: null, entry }] : [];
  }
  return FURNITURE_VIEW_ORDER.flatMap((view) => {
    const entry = family.views.get(view);
    return entry && (!entry.hidden || entry.id === id) ? [{ view, entry }] : [];
  });
}

/** Vue suivante (face → droite → dos → gauche → face), en sautant les vues absentes. Objet sans vues : lui-même. */
export function nextOrientation(id: string, step: 1 | -1 = 1): string {
  const list = orientations(id);
  const i = list.findIndex((o) => o.entry.id === id);
  if (i === -1 || list.length < 2) return id;
  return list[(i + step + list.length) % list.length]!.entry.id;
}

/** La même famille dans une autre vue (null si cette vue n'existe pas). */
export function withOrientation(id: string, view: FurnitureView): string | null {
  return orientations(id).find((o) => o.view === view)?.entry.id ?? null;
}

/** Le mobilier existe-t-il (catalogue ou tampon valide) ? Sans rien mémoriser — pour la validation serveur. */
export function isKnownFurniture(id: string): boolean {
  return byId.has(id) || parseStampId(id) !== null;
}

/**
 * Grille de collision d'un meuble posé (`map_furniture.collision_grid`) :
 * une chaîne par rangée de cases, du haut vers le bas, `'1'` = bloque.
 * Même orientation que `walkableTiles`.
 */
export type CollisionGrid = string[];

/** Taille max d'une grille acceptée par l'API (en cases). */
export const MAX_COLLISION_GRID_SIZE = 64;

export function isValidCollisionGrid(grid: unknown): grid is CollisionGrid {
  return Array.isArray(grid)
    && grid.length > 0
    && grid.length <= MAX_COLLISION_GRID_SIZE
    && grid.every((row) => typeof row === 'string' && row.length > 0 && row.length <= MAX_COLLISION_GRID_SIZE && /^[01]+$/.test(row));
}

/**
 * Seule la rangée du bas bloque : l'avatar peut se tenir sur les cases du
 * dessus et passe alors derrière l'objet (tri en Y) — portes vues de face,
 * armoires, arbres. Même convention que les murs.
 */
/**
 * Placement effectif d'un objet de carte : un objet debout bloque sa base et
 * se trie en Y (jamais « au sol »), même posé autrement avant l'ajout de la
 * règle (2026-10-14). « Toujours au-dessus » reste un choix de l'admin.
 */
export function standingPlacement<T extends { layer: string; isSolid: boolean; collisionGrid?: unknown }>(
  entry: FurnitureCatalogEntry | undefined,
  item: T,
): T {
  if (!entry?.standing) return item;
  return {
    ...item,
    layer: item.layer === 'furnitureTop' ? item.layer : 'furniture',
    isSolid: true,
    collisionGrid: entry.standing,
  };
}

export function baseOnlyCollisionGrid(widthTiles: number, heightTiles: number): CollisionGrid {
  const w = Math.max(1, widthTiles);
  const h = Math.max(1, heightTiles);
  return [...Array.from({ length: h - 1 }, () => '0'.repeat(w)), '1'.repeat(w)];
}


/** Bas de l'objet pour le tri en Y (px du monde) : `bottomY` remonté de `sortLift` (bâtiment à perron). */
export function furnitureSortY(entry: { sortLift?: number } | null | undefined, bottomY: number): number {
  return bottomY - (entry?.sortLift ?? 0);
}
